/**
 * Module principal Rénovation Globale d'Ampleur
 * Version avec support multi-lots (appartements)
 */
import { useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { HardHat, Building2, Home, Users, List, LayoutGrid } from 'lucide-react';
import { toast } from 'sonner';
import { prospectsAPI } from '@/lib/api';
import api from '@/lib/api';

// Import des composants
import { LotsManager } from './LotsManager';
import { ChantierSummaryBlock } from './ChantierSummaryBlock';
import { ChantierHistory } from './ChantierHistory';

// Import des étapes (pour mode simple sans lots)
import { EtudeTerrain } from './steps/EtudeTerrain';
import { DepotEtude } from './steps/DepotEtude';
import { AuditSubvention } from './steps/AuditSubvention';
import { AttenteDevis } from './steps/AttenteDevis';
import { GestionDevis } from './steps/GestionDevis';
import { SignatureElectronique } from './steps/SignatureElectronique';
import { PlanificationChantier } from './steps/PlanificationChantier';

// Sous-classes Rénovation Globale
const RENOVATION_SUBCLASSES = {
    personne_physique: { label: 'Personne physique', icon: Home },
    sci: { label: 'SCI', icon: Building2 },
    marchand_bien: { label: 'Société marchand de bien', icon: Building2 },
    fonciere: { label: 'Foncière', icon: Building2 },
    syndic: { label: 'Syndic', icon: Users },
    gestionnaire_patrimoine: { label: 'Gestionnaire de patrimoine', icon: Users },
    renovation_autre: { label: 'Autre', icon: Home }
};

export function RenovationGlobaleModule({ prospect, onUpdate }) {
    const [viewMode, setViewMode] = useState('lots'); // 'lots' ou 'simple'
    const [localHistory, setLocalHistory] = useState([]);
    
    // Données chantier du prospect
    const chantierData = useMemo(() => {
        return prospect?.chantier_data?.renovation_globale || {};
    }, [prospect?.chantier_data?.renovation_globale]);
    
    const lots = chantierData?.lots || [];
    const hasMultipleLots = lots.length > 1;
    
    // Pour le mode simple (un seul lot ou données legacy)
    const singleLotData = lots.length > 0 ? lots[0] : chantierData;
    
    const globalHistory = useMemo(() => {
        // Collecter l'historique de tous les lots
        const allHistory = chantierData?.history || [];
        lots.forEach(lot => {
            if (lot.history) {
                allHistory.push(...lot.history);
            }
        });
        return allHistory.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }, [chantierData?.history, lots]);
    
    const combinedHistory = useMemo(() => [...globalHistory, ...localHistory], [globalHistory, localHistory]);
    
    // Informations sur le sous-type
    const subclass = prospect?.prospect_subclass;
    const subclassInfo = RENOVATION_SUBCLASSES[subclass] || RENOVATION_SUBCLASSES.renovation_autre;
    const SubclassIcon = subclassInfo.icon;
    
    // Pour le mode simple
    const hasEtude = !!singleLotData?.etude_pdf?.id;
    const hasAudit = !!singleLotData?.audit_pdf?.id;
    const hasSubvention = !!singleLotData?.subvention_pdf?.id;
    const hasDevis = (singleLotData?.devis_list?.length || 0) > 0;
    const hasActiveDevis = !!singleLotData?.devis_actif_id;
    const canSendSignature = hasAudit && hasSubvention && hasActiveDevis;

    const handleHistoryAdd = useCallback(async (entry) => {
        const newEntry = {
            ...entry,
            id: `hist_${Date.now()}`,
            user_name: 'Utilisateur'
        };
        
        setLocalHistory(prev => [...prev, newEntry]);
        
        try {
            const currentHistory = chantierData?.history || [];
            await prospectsAPI.update(prospect.id, {
                chantier_data: {
                    ...prospect.chantier_data,
                    renovation_globale: {
                        ...chantierData,
                        history: [...currentHistory, newEntry]
                    }
                }
            });
        } catch (error) {
            console.error('Erreur sauvegarde historique:', error);
        }
    }, [prospect?.id, prospect?.chantier_data, chantierData]);

    const handleUpdate = useCallback(async () => {
        await onUpdate?.();
    }, [onUpdate]);

    // Compter les lots par statut
    const lotsStats = useMemo(() => {
        if (lots.length === 0) return null;
        return {
            total: lots.length,
            complete: lots.filter(l => l.signature?.status === 'signe' && l.planning?.date).length,
            signed: lots.filter(l => l.signature?.status === 'signe').length,
            pending: lots.filter(l => l.signature?.status === 'envoye' || l.signature?.status === 'consulte').length
        };
    }, [lots]);

    return (
        <div className="space-y-6" data-testid="renovation-globale-module">
            {/* En-tête avec type de chantier */}
            <Card className="border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-orange-100">
                                <HardHat className="w-6 h-6 text-orange-600" />
                            </div>
                            <div>
                                <CardTitle className="text-lg text-orange-900">
                                    Rénovation Globale d'Ampleur
                                </CardTitle>
                                <div className="flex items-center gap-2 mt-1">
                                    <SubclassIcon className="w-4 h-4 text-orange-600" />
                                    <span className="text-sm text-orange-700">{subclassInfo.label}</span>
                                    {lotsStats && (
                                        <Badge variant="outline" className="ml-2 text-orange-700 border-orange-300">
                                            {lotsStats.total} lot(s) • {lotsStats.signed} signé(s)
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Badge className="bg-orange-100 text-orange-700 border border-orange-200">
                                Parcours Rénovation
                            </Badge>
                        </div>
                    </div>
                </CardHeader>
            </Card>

            {/* Gestionnaire de Lots (multi-appartements) */}
            <LotsManager prospect={prospect} onUpdate={handleUpdate} />

            {/* Historique global */}
            {combinedHistory.length > 0 && (
                <ChantierHistory history={combinedHistory} maxVisible={5} />
            )}
        </div>
    );
}

export default RenovationGlobaleModule;
